import { useCallback, useState } from 'react';
import styled from 'styled-components';

import {
  ACCOUNT_NOT_ELIGIBLE,
  DimeApiError,
  IDENTITY_ALREADY_LINKED,
  LINK_LIMIT_REACHED,
} from '@tradeparadigm/terminal-react';

import { logException } from '@paradigm/logging/src/logging';

import { mintDimeEmbedToken } from '#/features/terminal/embed-token';
import type { DimeLinkPromptContext } from '@tradeparadigm/terminal-react';

/**
 * Offered once, after the Clerk fallback, to a Paradigm user whose identity
 * has no DIME link. The terminal package renders this in place of the
 * terminal until `dismiss` is called, so it fills whichever surface the user
 * opened (bottom tab or side panel).
 *
 * Linking is what makes every later visit silent: the package supplies the
 * Clerk-derived DIME JWT, this supplies the Paradigm embed token that only
 * this host can mint.
 */
export default function DimeLinkPrompt({
  linkIdentity,
  dismiss,
  onDismissed,
}: DimeLinkPromptContext & {
  /** Host-level dismissal, so the offer doesn't return on the next reopen. */
  readonly onDismissed: () => void;
}) {
  const [isLinking, setIsLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = useCallback(() => {
    onDismissed();
    dismiss();
  }, [onDismissed, dismiss]);

  const link = useCallback(async () => {
    setIsLinking(true);
    setError(null);
    // Minted per attempt: embed tokens live 5 minutes, and a click can land
    // well after the prompt was rendered. A failed mint has to throw here —
    // posting an empty token instead comes back as a 401, which the retry
    // below would read as a stale token and spend on a second doomed mint.
    const attempt = async () => {
      const token = await mintDimeEmbedToken();
      if (token == null || token.length === 0) {
        throw new Error('Embed token mint failed');
      }
      return linkIdentity(token);
    };
    try {
      await attempt();
      close();
    } catch (err) {
      if (err instanceof DimeApiError && err.code === IDENTITY_ALREADY_LINKED) {
        close();
        return;
      }
      // A bare 401 is the stale-token case rather than a refusal: mint again
      // and retry once before telling the user anything went wrong.
      if (err instanceof DimeApiError && err.status === 401) {
        try {
          await attempt();
          close();
          return;
        } catch (retryError) {
          setError(messageFor(retryError));
          setIsLinking(false);
          return;
        }
      }
      setError(messageFor(err));
      setIsLinking(false);
    }
  }, [linkIdentity, close]);

  return (
    <Centered>
      <Card>
        <Title>Link your Paradigm account</Title>
        <Body>
          Link this DIME Terminal account to your Paradigm login and you&apos;ll
          be signed in automatically from now on — no sign-in step.
        </Body>
        {error != null && <ErrorText role="alert">{error}</ErrorText>}
        <Actions>
          <SecondaryButton type="button" onClick={close} disabled={isLinking}>
            Not now
          </SecondaryButton>
          {/* `link` reports its own failures, so nothing here needs to
              handle a rejection. */}
          <PrimaryButton type="button" onClick={link} disabled={isLinking}>
            {isLinking ? 'Linking…' : 'Link account'}
          </PrimaryButton>
        </Actions>
      </Card>
    </Centered>
  );
}

/** The web-api's machine-readable codes are the host-integration contract. */
function messageFor(error: unknown): string {
  if (error instanceof DimeApiError) {
    switch (error.code) {
      case ACCOUNT_NOT_ELIGIBLE:
        return 'Finish setting up your DIME Terminal account first, then link from here.';
      case LINK_LIMIT_REACHED:
        return 'This DIME Terminal account has reached its limit of linked logins.';
      default:
        break;
    }
  }
  logException('dime-terminal', error);
  return 'Could not link your account. Please try again.';
}

const Centered = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 380px;
  padding: 20px;
  border: 1px solid var(--border-color, rgba(255, 255, 255, 0.12));
  border-radius: 8px;
  background: var(--bg-color-container, #16191c);
`;

const Title = styled.h3`
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--text-primary, #fff);
`;

const Body = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: var(--text-secondary, rgba(255, 255, 255, 0.65));
`;

const ErrorText = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: var(--error-color, #e3415a);
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
`;

const BaseButton = styled.button`
  padding: 6px 14px;
  font-size: 0.8125rem;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const PrimaryButton = styled(BaseButton)`
  border: none;
  background: var(--accent-color, #1bc1cf);
  color: var(--cta-text-color, #090a0a);

  &:hover:not(:disabled) {
    filter: brightness(1.08);
  }
`;

const SecondaryButton = styled(BaseButton)`
  border: 1px solid var(--border-color, rgba(255, 255, 255, 0.12));
  background: transparent;
  color: var(--text-secondary, rgba(255, 255, 255, 0.65));

  &:hover:not(:disabled) {
    color: var(--text-primary, #fff);
  }
`;
